import { useRouterState } from "@tanstack/react-router";
import { useEffect } from "react";

/**
 * Page metadata, declared once per route in `head()` and applied in two places: the
 * server writes it into the HTML (crawlers and link previews never run JavaScript), and
 * the browser keeps `document.head` in step while navigating.
 *
 * `canonical` and `og:url` are not declared: they are always the page's own path.
 */

type MetaTag =
  | { title: string }
  | { name: string; content: string }
  | { property: string; content: string };

export type Seo = {
  title: string;
  description: string;
  /** A path on this site or an absolute URL. The site icon when left out. */
  image?: string | null;
  /** Pages with no business in search results: shared-by-link lists, thin product pages. */
  noindex?: boolean;
  jsonLd?: Record<string, unknown>;
};

export const seo = ({ title, description, image, noindex, jsonLd }: Seo) => {
  const meta: MetaTag[] = [
    { title },
    { name: "description", content: description },
    { property: "og:type", content: "website" },
    { property: "og:site_name", content: "Listou" },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { name: "twitter:card", content: image ? "summary_large_image" : "summary" },
  ];
  // The fallback icon is square: declaring it 1200x630 makes previews stretch or crop it.
  if (image === undefined) {
    meta.push(
      { property: "og:image", content: "/icon-512.png" },
      { property: "og:image:width", content: "512" },
      { property: "og:image:height", content: "512" },
    );
  } else if (image) {
    meta.push(
      { property: "og:image", content: image },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
    );
  }
  if (noindex) meta.push({ name: "robots", content: "noindex" });
  return {
    meta,
    scripts: jsonLd ? [{ type: "application/ld+json", children: JSON.stringify(jsonLd) }] : [],
  };
};

/** The router types these as React element props; only a few plain fields are read. */
type HeadMatch = { meta?: Array<object | undefined>; headScripts?: Array<object | undefined> };
type Fields = Partial<
  Record<"title" | "name" | "property" | "content" | "type" | "children", unknown>
>;

type Head = {
  title: string | null;
  /** Keyed by `name:…` or `property:…`, the deepest route winning. */
  meta: Map<string, { attr: "name" | "property"; key: string; content: string }>;
  jsonLd: string[];
};

const collect = (matches: HeadMatch[]): Head => {
  const head: Head = { title: null, meta: new Map(), jsonLd: [] };
  for (const match of matches) {
    for (const tag of (match.meta ?? []) as Array<Fields | undefined>) {
      if (!tag) continue;
      if (typeof tag.title === "string") head.title = tag.title;
      const content = typeof tag.content === "string" ? tag.content : null;
      if (content === null) continue;
      if (typeof tag.name === "string") {
        head.meta.set(`name:${tag.name}`, { attr: "name", key: tag.name, content });
      } else if (typeof tag.property === "string") {
        head.meta.set(`property:${tag.property}`, { attr: "property", key: tag.property, content });
      }
    }
    // A deeper route's structured data describes the page better than its parent's.
    const scripts = ((match.headScripts ?? []) as Array<Fields | undefined>).filter(
      (script) => script?.type === "application/ld+json" && typeof script.children === "string",
    );
    if (scripts.length > 0) head.jsonLd = scripts.map((script) => String(script?.children));
  }
  return head;
};

const absolute = (origin: string, value: string) =>
  value.startsWith("/") ? origin + value : value;

const escapeHtml = (value: string) =>
  value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/**
 * `data-head` marks the tags the browser replaces on navigation: these, and index.html's
 * default description.
 */
export const renderHead = (matches: HeadMatch[], origin: string, pathname: string): string => {
  const head = collect(matches);
  if (head.title === null) return "";
  const url = origin + pathname;
  const tags = [`<title>${escapeHtml(head.title)}</title>`];
  for (const { attr, key, content } of head.meta.values()) {
    const value = key === "og:image" ? absolute(origin, content) : content;
    tags.push(`<meta ${attr}="${escapeHtml(key)}" content="${escapeHtml(value)}" data-head>`);
  }
  tags.push(
    `<meta property="og:url" content="${escapeHtml(url)}" data-head>`,
    `<link rel="canonical" href="${escapeHtml(url)}" data-head>`,
  );
  for (const json of head.jsonLd) {
    // JSON inside a script element: only "</script" can end it early.
    tags.push(
      `<script type="application/ld+json" data-head>${json.replace(/</g, "\\u003c")}</script>`,
    );
  }
  return tags.join("\n    ");
};

/** Keeps the document's head in step with the page while navigating in the browser. */
export function useDocumentHead() {
  // A string, so the effect runs when the metadata changes rather than on every router
  // state update (a background refetch, a hover preload).
  const signature = useRouterState({
    select: (state) =>
      JSON.stringify(
        state.matches.map((match) => ({ meta: match.meta, headScripts: match.headScripts })),
      ),
  });
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  useEffect(() => {
    const head = collect(JSON.parse(signature) as HeadMatch[]);
    if (head.title === null) return;
    const origin = window.location.origin;
    document.title = head.title;
    for (const element of document.head.querySelectorAll("[data-head]")) element.remove();

    const add = (element: HTMLElement) => {
      element.dataset.head = "";
      document.head.append(element);
    };
    const meta = (attr: string, key: string, content: string) => {
      const element = document.createElement("meta");
      element.setAttribute(attr, key);
      element.content = content;
      add(element);
    };
    for (const { attr, key, content } of head.meta.values()) {
      meta(attr, key, key === "og:image" ? absolute(origin, content) : content);
    }
    meta("property", "og:url", origin + pathname);
    const canonical = document.createElement("link");
    canonical.rel = "canonical";
    canonical.href = origin + pathname;
    add(canonical);
    for (const json of head.jsonLd) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.textContent = json;
      add(script);
    }
  }, [signature, pathname]);
}
